import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    withStyles,
    Button,
    Divider,
    CircularProgress,
    Typography,
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Dialog,
    Paper,
} from '@material-ui/core';
import { verify as verificationMethods, valuesDiskHealth } from '../../../sc-utilities/CustomerUtilities';
import BiosPasswordForm from './BiosPasswordForm';
import { Delete, ArrowUpward, ArrowDownward } from '@material-ui/icons';
import PresetWorkSettings from './PresetWorkSettings';
import { snackActions } from '../../../sc-utilities/SnackBar';
import { CancelToken } from 'axios';
import * as actions from '../../../sc-customer/Customer/store/customerActions';
import { securazeInitialValues } from '../index';
import * as skuActions from '../../../sc-settings/SKUPattern/store/skuPatternActions';
import AppLogoLoading from '../../../sc-logo';
import { enabledField } from '../index';

const styles = (theme) => ({
    root: {
        height: 'auto',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    paper: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    paperForm: {
        width: 'auto',
        height: 'auto',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        paddingTop: theme.spacing(2),
        marginLeft: theme.spacing(0),
        marginRight: theme.spacing(0),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(1),
    },
    submit: {
        width: '100%',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default,
        },
    },
    addButton: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default,
        },
    },
    wifiDivider: {
        margin: '15px 0px',
    },
});

const WorkPresets = (props) => {
    const {
        classes,
        activeCustomerId,
        translations,
        updatePresets,
        loadPresets,
        idParams,
        fetchOperatingSystems,
        customer,
        onFetchErasureMethods,
        onLoadCustomer,
        customerUpdatePresets,
        fetchSKUPattern,
    } = props;

    const [loading, setLoading] = useState(true);

    const [customerPresetFilter, setCustomerPresetsFilter] = useState({
        operatingSystems: [],
        methods: [],
        skuPatterns: [],
    });

    let presets = securazeInitialValues.Normal;
    if (customer?.Presets) {
        const customerPresets = JSON.parse(customer.Presets);
        if (customerPresets?.Normal) {
            presets = customerPresets.Normal;
        }
    }

    const initialValues = {
        PresetsEnabled: presets?.PresetsEnabled ?? false,
        AutoShutdown: presets?.AutoShutdown ?? true,
        MethodSSD: presets?.ErasureSSD?.Method ?? null,
        VerificationSSD: presets?.ErasureSSD?.Verification?.Method ?? null,
        CustomValueSSD: presets?.ErasureSSD?.Verification?.CustomValue ?? null,
        MethodHDD: presets?.ErasureHDD?.Method ?? null,
        VerificationHDD: presets?.ErasureHDD?.Verification?.Method ?? null,
        CustomValueHDD: presets?.ErasureHDD?.Verification?.CustomValue ?? null,
        automaticUnfreeze: presets?.automaticUnfreeze ?? false,
        UsePresetSmartScore: presets?.UsePresetSmartScore ?? false,
        PresetSmartScore: presets?.PresetSmartScore ?? null,
        OperatingSystemID: presets?.OperatingSystemID || '',
        NormalScreenBrightness: presets?.NormalScreenBrightness ?? 100,
        ReducedScreenBrightness: presets?.ReducedScreenBrightness ?? 80,
        SmartHealthCalculation: presets?.SmartHealthCalculation ?? null,
        SKUPatternID: presets?.SKUPatternID || '',
        PrintUponConnection: presets?.PrintUponConnection ?? false,
        PrintUponSuccessfulErasure: presets?.PrintUponSuccessfulErasure ?? false,
        PrintUponFailedOrAbortedErasure: presets?.PrintUponFailedOrAbortedErasure ?? false,
        PrintUponDiagnose: presets?.PrintUponDiagnose ?? false,
        BiosPasswords: presets?.BiosPasswords ?? { Enabled: false, Passwords: [] },
    };

    const customerCancel = CancelToken?.source();
    const osCancel = CancelToken.source();

    const collectPresets = (data) => {
        const Normal = {
            AutoShutdown: !!data?.AutoShutdown,
            NormalScreenBrightness: data?.NormalScreenBrightness ?? 100,
            ReducedScreenBrightness: data?.ReducedScreenBrightness ?? 80,
            ErasureSSD: {
                Method: data?.MethodSSD,
                Verification: {
                    Method: data?.VerificationSSD,
                    CustomValue: data?.CustomValueSSD,
                },
            },
            ErasureHDD: {
                Method: data?.MethodHDD,
                Verification: {
                    Method: data?.VerificationHDD,
                    CustomValue: data?.CustomValueHDD,
                },
            },
            automaticUnfreeze: !!data?.automaticUnfreeze,
            UsePresetSmartScore: !!data?.UsePresetSmartScore,
            PresetSmartScore: data?.PresetSmartScore,
            SmartHealthCalculation: data?.SmartHealthCalculation,
            OperatingSystemID: data?.OperatingSystemID,
            SKUPatternID: data?.SKUPatternID,
            PrintUponConnection: !!data?.PrintUponConnection,
            PrintUponSuccessfulErasure: !!data?.PrintUponSuccessfulErasure,
            PrintUponFailedOrAbortedErasure: !!data?.PrintUponFailedOrAbortedErasure,
            PrintUponDiagnose: !!data?.PrintUponDiagnose,
        };

        const presetData = {
            Normal: {
                ...Normal,
                BiosPasswords: {
                    ...data.BiosPasswords,
                    Passwords:
                        data.BiosPasswords.Passwords?.map((password, index) => {
                            return { ...password, Index: index };
                        }) ?? [],
                },
            },
            ...Normal,
        };
        return presetData;
    };

    const handleLoadPresets = () => {
        loadPresets
            ? loadPresets()
            : onLoadCustomer(
                  { CustomerID: activeCustomerId, ActiveCustomerID: activeCustomerId },
                  { cancelToken: customerCancel }
              );
    };

    const handleUpdatePresets = async (presets) => {
        return (await updatePresets)
            ? updatePresets({ Presets: presets, ...idParams, ActiveCustomerID: activeCustomerId })
            : customerUpdatePresets({
                  Presets: presets,
                  CustomerID: customer?.CustomerID,
                  ActiveCustomerID: activeCustomerId,
              });
    };

    useEffect(() => {
        const fetchData = async () => {
            let operatingSystems = await fetchOperatingSystems(
                { ActiveCustomerID: activeCustomerId, ...idParams },
                { cancelToken: osCancel }
            );
            let methods = await onFetchErasureMethods({ ActiveCustomerID: activeCustomerId });
            let skuPatterns = await fetchSKUPattern({
                ActiveCustomerID: activeCustomerId,
                q: { ProductType: 'PCProduct' },
            });

            if (operatingSystems && methods && skuPatterns) {
                setCustomerPresetsFilter({
                    operatingSystems: [
                        { value: null, displayValue: translations['none'] || 'None' },
                        ...operatingSystems,
                    ],
                    methods: [{ value: null, displayValue: translations['none'] || 'None' }, ...methods],
                    skuPatterns,
                });
            }
            setLoading(false);
        };

        handleLoadPresets();

        fetchData();

        return () => {
            customerCancel.cancel('Operation canceled by user');
            osCancel.cancel('Operation canceled by the user.');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCustomerId]);

    const biosPasswordObject = Yup.object().shape({
        Description: Yup.string().required(),
        Password: Yup.string().required(),
    });

    let formSettings = {
        Validation: {
            BiosPasswords: Yup.object().shape({
                Enabled: Yup.boolean().required(),
                Passwords: Yup.array().of(biosPasswordObject),
            }),
        },
        ButtonSubmit: {
            Title: translations['save'] || 'SAVE',
            Color: 'primary',
        },
    };

    const handleSubmit = async (data) => {
        const presets = collectPresets(data);

        const serverResult = await handleUpdatePresets(presets);
        handleLoadPresets();

        setLoading(false);
        if (serverResult.status) {
            return snackActions.snackBarComponent(translations['update_presets'] || 'Presets updated.', 'success');
        } else {
            return snackActions.snackBarComponent(
                translations['failed_to_update_presets'] || 'Error: Failed to update presets',
                'error'
            );
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
                open={loading}
                PaperProps={{
                    classes: {
                        root: classes.paper,
                    },
                }}
            >
                <AppLogoLoading />
            </Dialog>
            <Paper className={classes.paperForm}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        await handleSubmit(values);
                        setSubmitting(false);
                    }}
                    enableReinitialize
                    validationSchema={Yup.object().shape(formSettings.Validation)}
                >
                    {(formikProps) => {
                        const { values, isSubmitting, setFieldValue, handleChange, isValid, setValues, handleSubmit } =
                            formikProps;

                        const addBiosPassword = () => {
                            setValues({
                                ...values,
                                BiosPasswords: {
                                    Enabled: values.BiosPasswords.Enabled,
                                    Passwords: [...values.BiosPasswords.Passwords, { Description: '', Password: '' }],
                                },
                            });
                        };

                        const removeBiosPassword = (index) => {
                            setValues({
                                ...values,
                                BiosPasswords: {
                                    Enabled: values.BiosPasswords.Enabled,
                                    Passwords: values.BiosPasswords.Passwords.filter((value, i) => i !== index),
                                },
                            });
                        };

                        const moveItemInArray = (array, from, to) => {
                            if (to < 0) {
                                to = 0;
                            } else if (to >= array.length) {
                                to = array.length - 1;
                            }
                            const item = array.splice(from, 1)[0];
                            array.splice(to, 0, item);
                        };

                        const moveBiosPasswordUp = (index) => {
                            const passwords = values.BiosPasswords.Passwords;
                            moveItemInArray(passwords, index, index - 1);
                            setValues({
                                ...values,
                                BiosPasswords: { Enabled: values.BiosPasswords.Enabled, Passwords: passwords },
                            });
                        };

                        const moveBiosPasswordDown = (index) => {
                            const passwords = values.BiosPasswords.Passwords;
                            moveItemInArray(passwords, index, index + 1);
                            setValues({
                                ...values,
                                BiosPasswords: { Enabled: values.BiosPasswords.Enabled, Passwords: passwords },
                            });
                        };

                        const workProps = {
                            translations: translations,
                            handleChange: handleChange,
                            values: values,
                            setFieldValue: setFieldValue,
                            verify: verificationMethods,
                            methods: customerPresetFilter.methods,
                            operatingSystems: customerPresetFilter.operatingSystems,
                            setValues: setValues,
                            valuesDiskHealth: valuesDiskHealth,
                            skuPatterns: customerPresetFilter.skuPatterns,
                        };

                        return (
                            <form
                                className={classes.form}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }}
                            >
                                <PresetWorkSettings {...workProps} />

                                <Divider className={classes.divider} variant="middle" />
                                <br />

                                <Typography align="center" variant={'h5'}>
                                    {translations['remove_bios_uefi_passwords'] || 'Remove BIOS/UEFI Passwords'}
                                </Typography>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color={'primary'}
                                                margin={'dense'}
                                                name={'BiosPasswords.Enabled'}
                                                checked={values?.BiosPasswords?.Enabled || false}
                                                onChange={handleChange}
                                                disabled={enabledField(values)}
                                            />
                                        }
                                        label={`${translations['enabled'] || 'Enabled'}`}
                                    />
                                </FormGroup>
                                {values?.BiosPasswords?.Passwords?.map((password, index) => (
                                    <React.Fragment key={index}>
                                        <Grid
                                            container
                                            direction="row"
                                            justify="center"
                                            alignItems="center"
                                            spacing={5}
                                        >
                                            <Grid item xs={12} sm={9}>
                                                <BiosPasswordForm
                                                    translations={translations}
                                                    index={index}
                                                    formikProps={formikProps}
                                                />
                                            </Grid>
                                            <Grid container xs={12} sm={3} direction="row" justify="space-evenly">
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    onClick={() => moveBiosPasswordUp(index)}
                                                >
                                                    <ArrowUpward />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    onClick={() => moveBiosPasswordDown(index)}
                                                >
                                                    <ArrowDownward />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    onClick={() => removeBiosPassword(index)}
                                                >
                                                    <Delete />
                                                </Button>
                                            </Grid>
                                        </Grid>
                                        <Divider className={classes.wifiDivider} variant="middle" />
                                    </React.Fragment>
                                ))}
                                <Button
                                    className={classes.addButton}
                                    variant="contained"
                                    color={formSettings.ButtonSubmit.Color}
                                    type="button"
                                    disabled={!isValid}
                                    onClick={addBiosPassword}
                                >
                                    {translations['add_bios_uefi_password'] || 'Add BIOS/UEFI password'}
                                </Button>

                                <Divider className={classes.divider} variant="middle" />
                                <br />

                                <div>
                                    {formSettings.ButtonSubmit ? (
                                        <Button
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            disabled={!isValid || isSubmitting || loading}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                formSettings.ButtonSubmit.Title
                                            )}
                                        </Button>
                                    ) : null}
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </Paper>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomerId: state?.core?.login?.activeCustomer?.CustomerID || null,
        customer: state?.customer?.customers?.loaded || null,
        permissions: state?.core?.login?.permissions || [],
        translations: state?.translate?.translations?.activeTranslations || {},
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onLoadCustomer: (customerID, options) => dispatch(actions.loadCustomer(customerID, options)),
        customerUpdatePresets: (params, options) => dispatch(actions.updatePresets(params, options)),
        onFetchErasureMethods: (params) => dispatch(actions.fetchErasureMethod(params)),
        fetchOperatingSystems: (params, options) => dispatch(actions.fetchOperatingSystems(params, options)),
        fetchSKUPattern: (data) => dispatch(skuActions.fetchSKUPattern(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(WorkPresets));
